import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, AlertCircle, Plus, X, Check, CalendarClock, Building2,
  ArrowLeft, Trash2, Power,
} from "lucide-react";
import PledgeQrPanel from "../components/PledgeQrPanel";
import {
  useGetPledgeCohortsQuery,
  useCreatePledgeCohortMutation,
  useUpdatePledgeCohortMutation,
  useSetPledgeCohortStatusMutation,
  useDeletePledgeCohortMutation,
  type PledgeCohort,
} from "../api/graduatePledgeApi";

/**
 * Setting up the pledge form: which ceremony it collects for, which schools
 * may answer it, when it shuts, and the wording of what students agree to.
 *
 * One cohort is open at a time, and that is enforced on the server rather than
 * here — the public form has no cohort in its URL, so "which form is this"
 * must have exactly one answer. Opening a cohort therefore closes the others.
 *
 * A cohort that has taken submissions cannot be deleted. The reason to delete
 * one at all is a typo during set-up, which happens before the link goes out;
 * afterwards every row is somebody's declaration.
 */

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-indigo-200";

function fmt(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminGraduatePledgeCohortsPage() {
  const { data, isLoading, error } = useGetPledgeCohortsQuery();
  const [editing, setEditing] = useState<PledgeCohort | "new" | null>(null);

  const cohorts = data?.data ?? [];
  // The notice carries the dates of the cohort students would actually reach:
  // the open one, falling back to the newest so a sheet printed between
  // cohorts is not dateless.
  const openCohort = cohorts.find((c) => c.is_open) ?? cohorts[0];
  const institutes = data?.meta?.institutes ?? [];
  const publicUrl = data?.meta?.public_url ?? "";
  const defaults = {
    declaration: data?.meta?.default_declaration ?? "",
    consent: data?.meta?.default_consent ?? "",
  };

  return (
    <div className="p-4 sm:p-6 space-y-5 bg-white rounded-lg shadow-sm">

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <Link
            to="/admin/graduate-pledge"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to submissions
          </Link>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Pledge Cohorts</h2>
          <p className="text-xs text-gray-500 font-medium mt-1 max-w-2xl">
            One cohort per ceremony. The open cohort is the form students see at the public
            link — opening one closes the others.
          </p>
        </div>
        <button
          onClick={() => setEditing("new")}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 self-start"
        >
          <Plus className="w-4 h-4" /> New cohort
        </button>
      </div>

      {publicUrl && (
        <PledgeQrPanel
          url={publicUrl}
          cohortName={openCohort?.name}
          closesOn={openCohort ? fmt(openCohort.closes_on) : undefined}
          ceremonyDate={openCohort?.ceremony_date ? fmt(openCohort.ceremony_date) : undefined}
        />
      )}

      {!!error && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{(error as any)?.data?.message || "Could not load the cohorts."}</span>
        </div>
      )}

      {isLoading && (
        <div className="py-10 text-center"><Loader2 className="w-5 h-5 text-indigo-400 animate-spin mx-auto" /></div>
      )}

      {!isLoading && cohorts.length === 0 && (
        <div className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-slate-400">
          No cohort yet. Create one, pick the pilot institutions and set the cut-off date.
        </div>
      )}

      <div className="space-y-3">
        {cohorts.map((c) => (
          <CohortCard key={c.id} cohort={c} onEdit={() => setEditing(c)} />
        ))}
      </div>

      {editing && (
        <CohortDialog
          cohort={editing === "new" ? null : editing}
          institutes={institutes}
          defaults={defaults}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function CohortCard({ cohort, onEdit }: { cohort: PledgeCohort; onEdit: () => void }) {
  const [setStatus, { isLoading: toggling }] = useSetPledgeCohortStatusMutation();
  const [remove, { isLoading: deleting }] = useDeletePledgeCohortMutation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [err, setErr] = useState("");

  const empty = (cohort.submissions_count ?? 0) === 0;

  return (
    <div className="rounded-xl border p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-slate-800">{cohort.name}</h3>
            <span
              className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded border ${
                cohort.accepting
                  ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                  : "text-slate-500 bg-slate-100 border-slate-200"
              }`}
            >
              {cohort.accepting ? "Accepting" : "Closed"}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
            <span className="flex items-center gap-1">
              <CalendarClock className="w-3.5 h-3.5" />
              Closes {fmt(cohort.closes_on)}, 23:59
            </span>
            {cohort.ceremony_date && <span>Ceremony {fmt(cohort.ceremony_date)}</span>}
            <span className="flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5" />
              {cohort.institutions.length} institution{cohort.institutions.length === 1 ? "" : "s"}
            </span>
            <span>{cohort.submissions_count ?? 0} submission{(cohort.submissions_count ?? 0) === 1 ? "" : "s"}</span>
            <span className="font-mono">Next: {cohort.next_form_no}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              setErr("");
              try { await setStatus({ id: cohort.id, is_open: !cohort.is_open }).unwrap(); }
              catch (e: any) { setErr(e?.data?.message || "Could not change the status."); }
            }}
            disabled={toggling}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm disabled:opacity-40 ${
              cohort.is_open
                ? "text-slate-600 hover:bg-slate-50"
                : "text-emerald-700 border-emerald-200 hover:bg-emerald-50"
            }`}
          >
            {toggling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
            {cohort.is_open ? "Close form" : "Open form"}
          </button>

          <button onClick={onEdit} className="px-3 py-2 rounded-lg border text-sm text-gray-600 hover:bg-gray-50">
            Edit
          </button>

          {empty && (
            confirmDelete ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    setErr("");
                    try { await remove(cohort.id).unwrap(); }
                    catch (e: any) { setErr(e?.data?.message || "Could not delete."); }
                  }}
                  disabled={deleting}
                  className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-40"
                >
                  {deleting ? "Deleting…" : "Confirm"}
                </button>
                <button onClick={() => setConfirmDelete(false)} className="text-xs text-slate-500 underline">
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className="p-2 rounded-lg border border-red-200 text-red-500 hover:bg-red-50"
                title="Delete this empty cohort"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )
          )}
        </div>
      </div>

      {cohort.institutions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {cohort.institutions.map((i) => (
            <span key={i.id} className="text-[11px] px-2 py-1 rounded bg-slate-100 text-slate-600">
              {i.acronym || i.name}
            </span>
          ))}
        </div>
      )}

      {cohort.is_open && cohort.institutions.length === 0 && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          This cohort is open but has no institutions, so the dropdown on the student form is
          empty and nobody can submit. Add the pilot institutions.
        </div>
      )}

      {!cohort.accepting && cohort.is_open && cohort.closed_reason && (
        <div className="text-xs text-slate-500">{cohort.closed_reason}</div>
      )}

      {err && <div className="text-xs text-red-600">{err}</div>}
    </div>
  );
}

function CohortDialog({
  cohort, institutes, defaults, onClose,
}: {
  cohort: PledgeCohort | null;
  institutes: { id: number; name: string; acronym: string | null }[];
  defaults: { declaration: string; consent: string };
  onClose: () => void;
}) {
  const thisYear = new Date().getFullYear();

  const [name, setName] = useState(cohort?.name ?? `${thisYear} Graduate Pledge`);
  const [year, setYear] = useState(String(cohort?.year ?? thisYear));
  const [ceremony, setCeremony] = useState(cohort?.ceremony_date ?? "");
  const [closes, setCloses] = useState(cohort?.closes_on ?? "");
  const [ids, setIds] = useState<number[]>(cohort?.institutions.map((i) => i.id) ?? []);
  const [declaration, setDeclaration] = useState(cohort?.declaration_text ?? defaults.declaration);
  const [consent, setConsent] = useState(cohort?.consent_text ?? defaults.consent);
  const [error, setError] = useState("");

  const [create, { isLoading: creating }] = useCreatePledgeCohortMutation();
  const [update, { isLoading: updating }] = useUpdatePledgeCohortMutation();
  const busy = creating || updating;

  const toggle = (id: number) =>
    setIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const save = async () => {
    setError("");
    if (!name.trim() || !year || !closes) {
      setError("Name, year and the cut-off date are all required.");
      return;
    }
    const body = {
      name: name.trim(),
      year: Number(year),
      ceremony_date: ceremony || null,
      closes_on: closes,
      institution_ids: ids,
      declaration_text: declaration,
      consent_text: consent,
    };
    try {
      if (cohort) await update({ id: cohort.id, body }).unwrap();
      else await create(body).unwrap();
      onClose();
    } catch (e: any) {
      setError(e?.data?.message || "Could not save the cohort.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="font-bold text-slate-800">{cohort ? "Edit cohort" : "New cohort"}</div>
          <button onClick={onClose} className="p-2 rounded hover:bg-slate-100 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Name</label>
              <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                Year
              </label>
              <input
                type="number" className={inputCls} value={year}
                onChange={(e) => setYear(e.target.value)}
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Printed in every form number: NIQS/GPP/{year || "2026"}/0001
              </p>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                Ceremony date
              </label>
              <input type="date" className={inputCls} value={ceremony ?? ""} onChange={(e) => setCeremony(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                Submissions close on
              </label>
              <input type="date" className={inputCls} value={closes} onChange={(e) => setCloses(e.target.value)} />
              <p className="text-[11px] text-slate-400 mt-1">
                The form shuts automatically at 23:59 on this date — usually a week before the ceremony.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">
              Pilot institutions ({ids.length} selected)
            </label>
            <div className="max-h-52 overflow-y-auto rounded-lg border divide-y">
              {institutes.length === 0 && (
                <div className="px-3 py-4 text-xs text-slate-400">
                  No accredited institutes on the register yet.
                </div>
              )}
              {institutes.map((i) => (
                <label key={i.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    className="accent-indigo-600 w-4 h-4"
                    checked={ids.includes(i.id)}
                    onChange={() => toggle(i.id)}
                  />
                  <span className="text-sm text-slate-700">
                    {i.name}{i.acronym ? ` (${i.acronym})` : ""}
                  </span>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Only these appear in the student&rsquo;s dropdown. Removing one later does not remove
              the forms already submitted from it.
            </p>
          </div>

          <details className="rounded-lg border px-4 py-3">
            <summary className="text-sm font-semibold text-slate-700 cursor-pointer">
              Declaration and consent wording
            </summary>
            <div className="mt-3 space-y-3">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                  Declaration of Intent (Section C)
                </label>
                <textarea
                  rows={7} className={`${inputCls} font-mono text-xs`}
                  value={declaration} onChange={(e) => setDeclaration(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                  Data protection consent (Section D)
                </label>
                <textarea
                  rows={5} className={`${inputCls} font-mono text-xs`}
                  value={consent} onChange={(e) => setConsent(e.target.value)}
                />
              </div>
              <p className="text-[11px] text-slate-400">
                A student&rsquo;s submission keeps a copy of the wording they actually saw, so
                changing this never rewrites what somebody has already agreed to.
              </p>
            </div>
          </details>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-40"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save cohort
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
